// audio/engine.js: M5 Change #3.
//
// Web Audio API wrapper that the M2 audio/events bus mounts onto. The
// bus stays the call-site surface (emit/subscribe, see events.js); this
// engine is the listener that translates an emitted event name into a
// playable buffer.
//
// Goals:
//   • Single AudioContext, created lazily on first user interaction.
//     iOS rule: a context resumed by a user gesture survives subsequent
//     auto-play attempts (Safari/iOS-15+). createAudioContext at module
//     load would put the ctx in 'suspended' state; we'd have to play
//     after every gesture to keep it alive.
//   • Sound-tier gating, 'all' | 'vital' | 'off'. 'off' is short-circuit
//     (no fetch, no decode, no play). 'vital' still decodes everything
//     in the background (so tier-up doesn't pay decode latency).
//   • play(sampleId) NEVER throws and NEVER blocks. It either schedules
//     the buffer onto a fresh BufferSourceNode or silently drops. The
//     game loop must never wedge on audio.
//
// What this module does NOT do:
//   • It does NOT subscribe to events.js. That wiring lives in
//     audio/wire-engine.js (small file) so each surface can be unit-
//     tested separately.
//   • It does NOT ship sample data. M5 #4 ships card_snap.wav; #9 ships
//     the rest. A play() call for a missing sample logs once and
//     returns false.

/** @typedef {'all'|'vital'|'off'} SoundTier */

/**
 * @typedef {{
 *   buffer: AudioBuffer | null,
 *   vital: boolean,
 *   url: string,
 *   loadPromise: Promise<AudioBuffer | null> | null,
 *   failed: boolean,
 * }} Sample
 */

/**
 * @param {{
 *   tier?: SoundTier,
 *   AudioContextCtor?: typeof AudioContext,
 *   fetchFn?: typeof fetch,
 *   logger?: (level: 'info'|'warn'|'error', msg: string, meta?: any) => void,
 *   now?: () => number,
 * }} [opts]
 */
export function createAudioEngine(opts = {}) {
  /** @type {SoundTier} */
  let tier = opts.tier || 'all';
  const AudioCtor = opts.AudioContextCtor
    ?? (typeof globalThis !== 'undefined'
        ? (/** @type {any} */ (globalThis)).AudioContext
          ?? (/** @type {any} */ (globalThis)).webkitAudioContext
        : null);
  const fetchFn = opts.fetchFn
    ?? (typeof globalThis !== 'undefined' ? (/** @type {any} */ (globalThis)).fetch : null);
  const log = opts.logger ?? ((level, msg, meta) => {
    const fn = level === 'warn' ? console.warn
             : level === 'error' ? console.error
             : console.info;
    if (meta) fn(`[audio] ${msg}`, meta);
    else fn(`[audio] ${msg}`);
  });

  /** @type {AudioContext | null} */
  let ctx = null;
  /** @type {Map<string, Sample>} */
  const samples = new Map();
  // Sample IDs we've already warned about, keep the warning rate-limited.
  const warnedMissing = new Set();

  function ensureCtx() {
    if (ctx) return ctx;
    if (!AudioCtor) return null;
    try {
      ctx = new AudioCtor();
      return ctx;
    } catch (err) {
      log('warn', 'AudioContext construction failed; audio disabled', err);
      return null;
    }
  }

  /**
   * Register a sample. The sample's URL is fetched + decoded
   * asynchronously; play() resolves to silence if the sample isn't
   * loaded yet, then chimes in on next call.
   *
   * @param {string} sampleId
   * @param {string} url
   * @param {{ vital?: boolean }} [meta]
   */
  function register(sampleId, url, meta = {}) {
    if (samples.has(sampleId)) return;
    /** @type {Sample} */
    const s = {
      buffer: null,
      vital: !!meta.vital,
      url,
      loadPromise: null,
      failed: false,
    };
    samples.set(sampleId, s);
    if (tier !== 'off') beginLoad(sampleId);
  }

  function beginLoad(sampleId) {
    const s = samples.get(sampleId);
    if (!s || s.loadPromise || s.buffer || s.failed) return;
    const c = ensureCtx();
    if (!c || !fetchFn) {
      s.failed = true;
      return;
    }
    s.loadPromise = fetchFn(s.url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.arrayBuffer();
      })
      .then((ab) => c.decodeAudioData(ab))
      .then((buf) => {
        s.buffer = buf;
        s.loadPromise = null;
        return buf;
      })
      .catch((err) => {
        s.failed = true;
        s.loadPromise = null;
        log('warn', `sample failed to load: ${sampleId}`, err?.message ?? err);
        return null;
      });
  }

  /**
   * Play a sample.
   *
   * @param {string} sampleId
   * @param {{ playbackRate?: number, volume?: number }} [playOpts]
   * @returns {boolean}  true if a source node was started, false otherwise
   */
  function play(sampleId, playOpts = {}) {
    if (tier === 'off') return false;
    const s = samples.get(sampleId);
    if (!s) {
      if (!warnedMissing.has(sampleId)) {
        warnedMissing.add(sampleId);
        log('warn', `play('${sampleId}') — sample not registered`);
      }
      return false;
    }
    if (tier === 'vital' && !s.vital) return false;
    if (s.failed) return false;
    if (!s.buffer) {
      // Not yet decoded, quietly drop this call. Subsequent calls
      // after decode lands will play.
      return false;
    }
    const c = ensureCtx();
    if (!c) return false;
    if (c.state === 'suspended') {
      // No user gesture yet, best-effort attempt to resume, but don't
      // wait. play() must stay synchronous so the caller can fire it
      // inside an rAF tick.
      c.resume().catch(() => {});
      return false;
    }
    try {
      const src = c.createBufferSource();
      src.buffer = s.buffer;
      if (typeof playOpts.playbackRate === 'number') src.playbackRate.value = playOpts.playbackRate;
      let node = /** @type {AudioNode} */ (src);
      if (typeof playOpts.volume === 'number') {
        const g = c.createGain();
        g.gain.value = playOpts.volume;
        src.connect(g);
        node = g;
      }
      node.connect(c.destination);
      src.start();
      return true;
    } catch (err) {
      log('warn', `play('${sampleId}') threw`, err);
      return false;
    }
  }

  /**
   * Resume the AudioContext. Call from a `pointerdown` / `keydown`
   * listener so iOS lifts the suspend.
   */
  async function unlock() {
    const c = ensureCtx();
    if (!c) return;
    if (c.state === 'suspended') {
      try { await c.resume(); } catch (err) {
        log('warn', 'AudioContext.resume() rejected', err);
      }
    }
  }

  function setTier(next) {
    if (next === tier) return;
    tier = next;
    if (next !== 'off') {
      for (const sampleId of samples.keys()) beginLoad(sampleId);
    }
  }

  function getTier() { return tier; }

  function getContextState() {
    return ctx ? ctx.state : 'closed';
  }

  function isSampleLoaded(sampleId) {
    const s = samples.get(sampleId);
    return !!(s && s.buffer);
  }

  function dispose() {
    if (ctx) {
      try { ctx.close(); } catch { /* ignore */ }
      ctx = null;
    }
    samples.clear();
    warnedMissing.clear();
  }

  return {
    register,
    play,
    unlock,
    setTier,
    getTier,
    getContextState,
    isSampleLoaded,
    dispose,
  };
}
